// ============================================================
// thermal.js
// EV Powertrain Simulator — core 모듈
// 열관리 — 모터/배터리/인버터 열 모델, 히트펌프 COP, 냉각수 제어
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================


// ── thermalStep ──
/**
 * 4노드 집중정수 열 모델 1스텝 (배터리·인버터·모터·냉각수)
 *
 * 4개 열 노드를 오일러 적분으로 업데이트한다:
 *   dT_batt/dt  = (Q_batt − Q_bc − Q_ba) / C_th_batt
 *   dT_inv/dt   = (Q_inv  − Q_ic − Q_ia) / C_th_inv
 *   dT_motor/dt = (Q_motor − Q_mc)        / C_th_motor
 *   dT_cool/dt  = (Q_bc + Q_ic + Q_mc − Q_rad) / C_th_cool
 * 수치 불안정 방지를 위해 CFL 기반 적응형 서브스텝(최대 10회)을 적용한다.
 * phi = phi_speed × phi_flow로 방열 계수를 합성하며 params.phi_max로 상한 클램핑.
 *
 * @param {object} thState   - 현재 열 상태 { T_batt, T_inv, T_motor, T_cool }
 * @param {number} Q_batt    - 배터리 발열 [W]
 * @param {number} Q_inv     - 인버터 발열 [W]
 * @param {number} Q_motor   - 모터 발열 [W]
 * @param {number} v         - 차량 속도 [m/s] (방열 계수 계산용)
 * @param {object} params    - 차량 파라미터 (C_th_*, R_th_*, UA_rad 등)
 * @param {number} dt        - 타임스텝 [s]
 * @param {number} [phi_flow_override] - 냉각수 유량 방열 계수 (없으면 1.0)
 * @returns {{ T_batt, T_inv, T_motor, T_cool }}
 */
function thermalStep(thState, Q_batt, Q_inv, Q_motor, v, params, dt, phi_flow_override) {
  const { T_batt, T_inv, T_motor, T_cool } = thState;
  const T_amb = params.T_amb;
  // 속도 의존 방열 계수 (기존)
  const phi_speed = 1 + params.phi_speed * Math.min(v / 30, 2);
  // [R-13] 냉각수 유량 의존 방열 계수 (새로 추가): phi_flow_override 없으면 1.0
  const phi_flow  = (phi_flow_override !== undefined && phi_flow_override > 0)
    ? Math.min(phi_flow_override, 2.0)
    : 1.0;
  // 합성 방열 계수: 속도 × 유량 (물리적으로 독립적이므로 곱셈)
  // [Fix-Phi-Clamp] 상한 클램프 추가: phi_speed(최대 3.0) × phi_flow(최대 2.0) = 6.0 비제한 →
  //   UA_rad×phi×ΔT 과대로 냉각수온 급변 가능. params.phi_max (기본 4.0)으로 제한.
  //   4.0 = 속도 60km/h(phi_speed≈2.0) + 유량 2× 상한을 커버하는 물리 합리 범위.
  const phi = Math.min(phi_speed * phi_flow, params.phi_max || 4.0);
  const Q_rad = params.UA_rad * phi * (T_cool - T_amb);
  // [Fix-Rth] 유량 의존 접촉 열저항: R_th ∝ 1/flow^n (Dittus-Boelter 근사, n=0.6)
  // phi_flow=1.0 기준 시 R_th 변화 없음(하위 호환). 유량 증가 → R_th 감소 → 냉각 강화.
  // n=0.6: 완전 발달 난류(n=0.8)보다 보수적 — 실제 EV 냉각채널의 부분 발달 유동 반영.
  const _rth_mod = Math.pow(Math.max(0.3, Math.min(phi_flow, 2.5)), -0.6);
  const R_th_bc_eff = params.R_th_bc * _rth_mod;
  const R_th_ic_eff = params.R_th_ic * _rth_mod;
  const R_th_mc_eff = params.R_th_mc * _rth_mod;
  // [Fix-⑧] 인버터 직접 방열 경로 추가
  //   실제 인버터는 히트싱크를 통해 직접 외기로 방열함 (냉각수 경로와 병렬)
  //   R_th_ia: 인버터→외기 직접 열저항 (기본값 0.5 K/W — 냉각수 경로보다 크지만 무시불가)
  //   params.R_th_ia = 0 설정 시 기존 동작 유지 (직접 방열 없음)
  const R_th_ia_eff = params.R_th_ia && params.R_th_ia > 0 ? params.R_th_ia : Infinity;
  const Q_ia = R_th_ia_eff < 1e9 ? (T_inv - T_amb) / R_th_ia_eff : 0; // 인버터→외기 직접 방열
  // Heat flows
  const Q_bc = (T_batt - T_cool) / R_th_bc_eff;
  const Q_ba = (T_batt - T_amb) / params.R_th_ba;
  const Q_ic = (T_inv - T_cool) / R_th_ic_eff;
  const Q_mc = (T_motor - T_cool) / R_th_mc_eff;
  // Temperature derivatives — [Fix-⑧] Q_ia 인버터 직접 방열 반영
  // [Fix-EulerStab] 소열질량 노드 수치 불안정 방지 — 적응형 서브스텝
  // CFL 조건: dt_sub ≤ 0.5 × τ_min,  τ = C/ΣUA (가장 작은 노드 기준)
  // UA_bc/ic/mc = 1/R_th, UA_ba = 1/R_th_ba
  const UA_batt  = 1/R_th_bc_eff + 1/params.R_th_ba;
  const UA_inv   = 1/R_th_ic_eff + (R_th_ia_eff < 1e9 ? 1/R_th_ia_eff : 0);
  const UA_motor = 1/R_th_mc_eff;
  const UA_cool  = 1/R_th_bc_eff + 1/R_th_ic_eff + 1/R_th_mc_eff
                 + (params.UA_rad ? params.UA_rad : 0);
  const tau_batt  = UA_batt  > 0 ? params.C_th_batt  / UA_batt  : Infinity;
  const tau_inv   = UA_inv   > 0 ? params.C_th_inv   / UA_inv   : Infinity;
  const tau_motor = UA_motor > 0 ? params.C_th_motor / UA_motor : Infinity;
  const tau_cool  = UA_cool  > 0 ? params.C_th_cool  / UA_cool  : Infinity;
  const tau_min   = Math.min(tau_batt, tau_inv, tau_motor, tau_cool);
  // 서브스텝 수: 최대 10 (성능 보호), 최소 1
  const n_sub  = Math.min(10, Math.max(1, Math.ceil(dt / (0.5 * tau_min))));
  const dt_sub = dt / n_sub;

  let Tb = T_batt, Ti = T_inv, Tm = T_motor, Tc = T_cool;
  for (let _s = 0; _s < n_sub; _s++) {
    const qbc = (Tb - Tc) / R_th_bc_eff;
    const qba = (Tb - T_amb) / params.R_th_ba;
    const qic = (Ti - Tc) / R_th_ic_eff;
    const qia = R_th_ia_eff < 1e9 ? (Ti - T_amb) / R_th_ia_eff : 0;
    const qmc = (Tm - Tc) / R_th_mc_eff;
    const qrad = params.UA_rad * phi * (Tc - T_amb);
    Tb += (Q_batt  - qbc - qba) / params.C_th_batt  * dt_sub;
    Ti += (Q_inv   - qic - qia) / params.C_th_inv   * dt_sub;
    Tm += (Q_motor - qmc)       / params.C_th_motor * dt_sub;
    // [Fix-INFO-1] 냉각수 최저 온도 하한 파라미터화
    //   수정 전: T_amb - 5 하드코딩 → 칠러(chiller) 활성 냉각 시뮬레이션에서 부적절하게 제한
    //   수정 후: params.T_cool_min_offset (기본값 -5°C) → 하위 호환 보장, 칠러 시나리오에서 조정 가능
    const _T_cool_min_offset = params.T_cool_min_offset ?? -5;
    Tc  = Math.max(T_amb + _T_cool_min_offset,
          Tc + (qbc + qic + qmc - qrad) / params.C_th_cool * dt_sub);
  }
  return {
    T_batt:  Tb,
    T_inv:   Ti,
    T_motor: Tm,
    T_cool:  Tc,
  };
}

// ── thermalDeratingFactor ──
/**
 * 모터·배터리·인버터 온도에 따른 출력 디레이팅 계수를 계산한다.
 * 각 노드의 온도가 derate_start 이상이면 선형으로 감소하고 cutoff에서 0이 된다.
 * f_total은 세 계수 중 최솟값 (병목 부품이 전체 출력을 제한).
 *
 * @param {{ T_motor: number, T_batt: number, T_inv: number }} thState  현재 열 상태
 * @param {object} params
 *   params.T_motor_derate_start, T_motor_cutoff — 모터 디레이팅 구간 [°C]
 *   params.T_batt_derate_start,  T_batt_cutoff  — 배터리 디레이팅 구간 [°C]
 *   params.T_inv_derate_start,   T_inv_cutoff   — 인버터 디레이팅 구간 [°C]
 * @returns {{ f_motor: number, f_batt: number, f_inv: number, f_total: number }}
 */
function thermalDeratingFactor(thState, params) {
  const T_m = thState.T_motor || 25;
  const T_b = thState.T_batt  || 25;
  const T_i = thState.T_inv   || 25;
  // Motor derating
  const T_m_derate = params.T_motor_derate_start || 140;
  const T_m_cutoff = params.T_motor_cutoff || 160;
  const f_motor = T_m < T_m_derate ? 1.0
    : T_m > T_m_cutoff ? 0.0
    : 1.0 - (T_m - T_m_derate) / (T_m_cutoff - T_m_derate);
  // Battery derating
  const T_b_derate = params.T_batt_derate_start || 45;
  const T_b_cutoff = params.T_batt_cutoff || 60;
  const f_batt = T_b < T_b_derate ? 1.0
    : T_b > T_b_cutoff ? 0.0
    : 1.0 - (T_b - T_b_derate) / (T_b_cutoff - T_b_derate);
  // Inverter
  const T_i_derate = params.T_inv_derate_start || 80;
  const T_i_cutoff = params.T_inv_cutoff || 100;
  const f_inv = T_i < T_i_derate ? 1.0
    : T_i > T_i_cutoff ? 0.0
    : 1.0 - (T_i - T_i_derate) / (T_i_cutoff - T_i_derate);
  return {
    f_motor: +Math.max(0, f_motor).toFixed(3),
    f_batt:  +Math.max(0, f_batt).toFixed(3),
    f_inv:   +Math.max(0, f_inv).toFixed(3),
    f_total: +Math.max(0, Math.min(f_motor, f_batt, f_inv)).toFixed(3),
  };
}

// ── hvacDetailedPower ──
/**
 * HVAC 전력 소비 계산.
 *
 * [Fix-Th2] params 직접 변이 제거: `_hvac_prev_mode`를 params에 쓰지 않고
 * 반환값의 `new_hvac_mode` 필드로 노출한다.
 * 호출자는 히스테리시스를 유지하려면 다음 두 방법 중 하나를 선택한다:
 *   A) 별도 state 객체를 관리  →  `const hvacState = {}; … hvacState._hvac_prev_mode = result.new_hvac_mode;`
 *   B) params 업데이트  →  `params._hvac_prev_mode = result.new_hvac_mode;` (기존 방식 유지 시)
 *
 * 하위 호환: 반환값은 숫자 대신 `{ P, new_hvac_mode }` 객체.
 * 기존 호출부가 숫자로 쓰는 경우에는 `result.P` 로 접근해야 한다.
 *
 * @param {number} T_amb
 * @param {number} v
 * @param {number} soc
 * @param {object} params
 * @param {object} [hvac_state]  `{ _hvac_prev_mode }` 선택적 외부 상태 객체.
 *   미전달 시 params._hvac_prev_mode를 읽기 전용으로 참조 (쓰기 없음).
 * @returns {{ P: number, new_hvac_mode: string }}
 */
function hvacDetailedPower(T_amb, v, soc, params, hvac_state) {
  const mode = params.hvac_mode || 'auto';
  const P_base = params.hvac_base_power || 300;
  const P_max  = params.hvac_max_power  || 4500;

  // [Fix-HVAC-Hysteresis] 단일 임계값 채터링 방지 — ±1°C 데드밴드 적용
  // [Fix-Th2] hvac_state가 있으면 그 쪽에서 prevMode를 읽고, 없으면 params에서 읽기만 함 (쓰기 없음)
  const stateRef = hvac_state || {};
  const prevMode = stateRef._hvac_prev_mode ?? params._hvac_prev_mode ?? 'none';
  let needHeat, needCool;
  if (mode === 'heat') {
    needHeat = true; needCool = false;
  } else if (mode === 'cool') {
    needHeat = false; needCool = true;
  } else {
    // auto — 히스테리시스 판정
    if (prevMode === 'heat') {
      needHeat = T_amb < 11;   // 난방 해제 임계: 11°C
    } else {
      needHeat = T_amb < 10;   // 난방 진입 임계: 10°C
    }
    if (prevMode === 'cool') {
      needCool = T_amb > 24;   // 냉방 해제 임계: 24°C
    } else {
      needCool = T_amb > 25;   // 냉방 진입 임계: 25°C
    }
    if (needHeat && needCool) needCool = false; // 중복 방지
  }
  // [Fix-Th2] 현재 모드를 반환값으로 노출 — params 직접 변이 제거
  const new_hvac_mode = needHeat ? 'heat' : needCool ? 'cool' : 'none';

  let P_hvac = P_base;
  if (needHeat) {
    // [A25-Fix6] 단순 선형 COP → heatPumpCOP() 카르노 기반 모델 (냉매·제상·등엔트로피 반영)
    // [Th-L1] Q_max_T: 극저온 용량 한계 — 실제 필요 난방량과 용량 한계 중 작은 값 사용
    const copResult = heatPumpCOP(T_amb, 'heat', params);
    const COP = copResult.COP;
    const Q_heat_needed = Math.min(3000, 3000 * Math.max(0, 10 - T_amb) / 25 * (soc > 0.2 ? 1 : 0.5));
    const Q_heat = Math.min(Q_heat_needed, copResult.Q_max_T);  // [Th-L1] 용량 한계 적용
    P_hvac = P_base + Math.max(0, Q_heat) / COP;
  } else if (needCool) {
    // [A25-Fix6] 냉방도 heatPumpCOP() 일관 적용
    const copResult = heatPumpCOP(T_amb, 'cool', params);
    const COP = copResult.COP;
    const Q_cool = Math.min(copResult.Q_max_T, Math.min(4000, 2000 * (T_amb - 25) / 15));
    P_hvac = P_base + Math.max(0, Q_cool) / COP;
  }

  // 속도 의존: 고속 주행 시 냉각팬 전력
  P_hvac += (params.hvac_speed_coeff || 0) * v;

  return {
    P: Math.min(P_max, Math.max(P_base, P_hvac)),
    new_hvac_mode,
  };
}

// ── heatPumpCOP ──
/**
 * 히트펌프 COP + [Th-L1] 극저온 용량 한계 (Q_max_T)
 *
 * 기존: COP만 반환 → hvacDetailedPower에서 Q_heat를 고정 3 kW 가정
 * 추가: Q_max_T [W] — 냉매 흡입 압력 저하로 인한 압축기 용량 한계
 *   냉난방 능력은 COP보다 먼저 천장에 부딪힘:
 *   Q_max(T) = Q_max_nom × f_cap(T)
 *   f_cap:  T ≥ T_cap_nom → 1.0 (정격)
 *           T_cap_limit ≤ T < T_cap_nom → 선형 감소 (1.0 → f_cap_min)
 *           T < T_cap_limit → f_cap_min (최소 용량 유지)
 *
 * 파라미터 (가열 기준, 기본값은 대형 EV용 R744 열펌프 기준):
 *   params.hp_Q_max_nom     : 정격 가열 용량 [W] (기본 6000 W)
 *   params.hp_T_cap_nom     : 용량 정격 기준 온도 [°C] (기본 0°C)
 *   params.hp_T_cap_limit   : 최소 용량 도달 온도 [°C] (기본 -25°C)
 *   params.hp_f_cap_min     : 최소 용량 비율 (기본 0.25 = 25%)
 *
 * 참고: SAE J2997; Hara et al. (2012) "Heat Pump System for EV" SAE 2012-01-1207
 */
function heatPumpCOP(T_amb, mode, params) {
  // 냉매 모델 선택 ('R744'=CO2 초임계, 'R134a'=일반, 'R1234yf')
  const refrigerant   = params.hp_refrigerant || 'R744';
  const T_target      = params.T_hp_target    || 22;      // 실내 목표온도 (°C)
  const eta_hp_heat   = params.eta_hp_heat    || 0.47;    // 압축기 등엔트로피 효율 (가열)
  const eta_hp_cool   = params.eta_hp_cool    || 0.52;    // 압축기 등엔트로피 효율 (냉각)

  // 증발기/응축기 온도 (실제 열교환 포함 여유온도)
  const dT_lift_heat   = 8;   // 응축기 → 실내 온도차 [K]
  const dT_lift_evap   = 6;   // 외기 → 증발기 온도차 [K]

  let COP_actual;
  if (mode === 'heat') {
    const T_evap_K = Math.max(243.15, (T_amb - dT_lift_evap) + 273.15);  // 증발기 [K]
    const T_cond_K = (T_target + dT_lift_heat) + 273.15;                  // 응축기 [K]
    const dT       = Math.max(1, T_cond_K - T_evap_K);
    const COP_carnot = T_cond_K / dT;
    // R744: 초임계 초저온 보정 (−20°C 이하에서 +15%)
    const ref_boost  = (refrigerant === 'R744' && T_amb < -10) ? 1.15 : 1.0;
    // 서霜(제상) 주기 손실 (−5°C ~ 5°C 구간에서 −20% 패널티)
    const defrost_loss = (T_amb > -5 && T_amb < 5) ? 0.82 : 1.0;
    COP_actual = Math.max(1.0, COP_carnot * eta_hp_heat * ref_boost * defrost_loss);
  } else {
    // cooling mode
    const T_evap_K = (T_target - dT_lift_evap) + 273.15;
    const T_cond_K = Math.min(360, (T_amb + dT_lift_heat) + 273.15);
    const dT       = Math.max(1, T_cond_K - T_evap_K);
    const COP_carnot = T_evap_K / dT;
    // [Fix-R744] R744 초임계 냉방 보정: T_amb > 31.1°C(임계점)에서 응축 사이클 불가.
    // 가스쿨러(gas cooler) 방식으로 전환 → 아임계 대비 COP 손실 발생.
    // 경험적 보정: T_amb 31→51°C 구간에서 COP × 0.85~0.92 선형 감소.
    const T_crit_R744 = 31.1;
    const gc_penalty = (refrigerant === 'R744' && T_amb > T_crit_R744)
      ? 0.92 - 0.007 * Math.min(T_amb - T_crit_R744, 20)   // max −14% at 51°C
      : 1.0;
    COP_actual = Math.max(1.0, COP_carnot * eta_hp_cool * gc_penalty);
  }

  // [Th-L1] 극저온 가열 용량 한계 (난방 모드 전용)
  // 냉매 흡입 압력이 저하하면 압축기가 부피 유량이 고정되어 있어 질량 유량 감소 → 용량 저하
  // 이 효과는 COP보다 먼저(더 높은 온도에서) 나타남
  const Q_max_nom   = params.hp_Q_max_nom    || 6000;   // [W] 정격 가열 용량
  const T_cap_nom   = params.hp_T_cap_nom    || 0;      // [°C] 정격 온도 하한
  const T_cap_limit = params.hp_T_cap_limit  || -25;    // [°C] 최소 용량 도달 온도
  const f_cap_min   = params.hp_f_cap_min    || 0.25;   // 최소 용량 비율
  let f_cap = 1.0;
  if (mode === 'heat' && T_amb < T_cap_nom) {
    const range = Math.max(1, T_cap_nom - T_cap_limit);
    f_cap = Math.max(f_cap_min,
      f_cap_min + (1 - f_cap_min) * Math.max(0, T_amb - T_cap_limit) / range);
  }
  const Q_max_T = mode === 'heat' ? Q_max_nom * f_cap : (params.hp_Q_cool_max || 8000);

  return {
    COP: COP_actual,
    Q_max_T,                     // [Th-L1] 현재 외기온에서의 최대 열출력 [W]
    f_cap: +f_cap.toFixed(3),    // [Th-L1] 용량 감소 비율 (1.0=정격)
    // 참고용: 공칭 COP 상한 클램프 (슬라이더 한도 반영)
    COP_ref: mode === 'heat'
      ? Math.min(params.cop_hp_heat_ref || 2.5, COP_actual)
      : Math.min(params.cop_hp_cool_ref || 3.0, COP_actual),
  };
}

// ── gearThermalStep ──
/**
 * 기어박스 온도를 1스텝 진행한다 (1차 집중정수 열 모델).
 * Q_rad = (T_gear − T_amb) / (R_th / phi) 로 방열하고, 나머지가 온도를 상승시킨다.
 *
 * @param {number} T_gear      현재 기어박스 온도 [°C]
 * @param {number} P_loss_gear 기어 손실 [W]
 * @param {number} v           차속 [m/s] (방열 향상 계수)
 * @param {object} params      gear_C_th [J/K], gear_R_th [K/W], T_amb [°C]
 * @param {number} dt          타임스텝 [s]
 * @returns {number}           다음 기어박스 온도 [°C]
 */
function gearThermalStep(T_gear, P_loss_gear, v, params, dt) {
  const C_th = params.gear_C_th || 2000;
  const R_th = params.gear_R_th || 0.15;
  const phi  = 1 + 0.02 * Math.min(v / 20, 3);  // 속도에 따른 방열 향상
  const Q_rad = (T_gear - (params.T_amb || 25)) / (R_th / phi);
  const dT    = (P_loss_gear - Q_rad) / C_th * dt;
  return Math.max(params.T_amb || 25, T_gear + dT);
}

// ── initCoolantFlowState ──
/**
 * 냉각수 유량 제어 상태를 초기화한다.
 * @param {object} params  coolant_flow_base [L/min]
 * @returns {{ flow_rate: number, integral: number, pump_power: number }}
 */
function initCoolantFlowState(params) {
  return {
    flow_rate: params.coolant_flow_base || 10,
    integral: 0,
    pump_power: 0,
  };
}

// ── coolantFlowControl ──
/**
 * PI 제어기로 냉각수 유량을 결정하고 펌프 전력을 계산한다.
 * coolant_flow_opt_enable=false 시 일정 유량 모드(기본 동작)로 동작한다.
 * Anti-windup: 적분항을 (flow_max−flow_base)/Ki ~ (flow_min−flow_base)/Ki 구간으로 클램프.
 * 펌프 전력: P_pump ∝ flow² (원심 펌프 특성).
 *
 * @param {{ flow_rate: number, integral: number }} cool_state  이전 제어 상태
 * @param {number} T_cool  현재 냉각수 온도 [°C]
 * @param {object} params  coolant_T_target, flow_base/max/min, pump_power_coeff 등
 * @param {number} dt      타임스텝 [s]
 * @returns {{ flow_rate, integral, pump_power, phi_flow, T_error, alarm }}
 */
function coolantFlowControl(cool_state, T_cool, params, dt) {
  if (!params.coolant_flow_opt_enable) {
    return { ...cool_state, pump_power: (params.coolant_pump_power_coeff || 12) * cool_state.flow_rate };
  }
  const T_tgt = params.coolant_T_target || 45;
  const e     = T_cool - T_tgt;               // 온도 오차 (양수=과열)
  const Kp    = 0.3, Ki = 0.05;
  // [Fix-AW] Anti-windup: 적분항 클램프 — 유량 포화 시 integral 발산 방지.
  // 한계값 = (flow_max − flow_base) / Ki 로부터 역산한 적분 포화 경계.
  const flow_base = params.coolant_flow_base || 10;
  const flow_max  = params.coolant_flow_max  || 25;
  const flow_min  = params.coolant_flow_min  || 3;
  const I_MAX = (flow_max - flow_base) / Ki;   // ≈ +300 (기본값 기준)
  const I_MIN = (flow_min - flow_base) / Ki;   // ≈ −140 (기본값 기준)
  const integral_new = Math.max(I_MIN, Math.min(I_MAX, cool_state.integral + e * dt));
  const flow_demand  = flow_base + Kp * e + Ki * integral_new;
  const flow_rate    = Math.min(flow_max, Math.max(flow_min, flow_demand));
  // 펌프 전력: P_pump ≈ k · flow²  (원심펌프 근사)
  const k_pump = params.coolant_pump_power_coeff || 12;
  const pump_power = k_pump * (flow_rate / 10) * (flow_rate / 10) * 10;  // 기준 유량 대비

  // 방열 성능 향상: flow 증가 → UA_rad 증가
  const phi_flow = 0.8 + 0.2 * Math.min(flow_rate / (params.coolant_flow_base || 10), 2);

  return {
    flow_rate: +flow_rate.toFixed(2),
    integral: integral_new,
    pump_power: +pump_power.toFixed(1),
    phi_flow: +phi_flow.toFixed(3),
    T_error: +e.toFixed(2),
    alarm: T_cool > (params.coolant_T_alarm || 65),
  };
}

// ── init3DThermalState ──
/**
 * 3D 배터리 팩 열격자 상태를 초기화한다.
 * BMS 셀 수(bms_cell_count_s × bms_cell_count_p)와 3D 격자(nx × ny × nz)가
 * 불일치하면 즉시 Error를 throw하여 파라미터 오류를 초기화 시점에 노출한다.
 *
 * @param {object} params
 *   params.bms_cell_count_s/p — 직·병렬 셀 수
 *   params.thermal_3d_nx/ny/nz — 격자 크기 (기본 8×6×2)
 *   params.T_amb — 초기 셀 온도 [°C]
 * @returns {{ T_cells: number[], T_max, T_min, T_avg, T_delta, hotspot_index, n_x, n_y, n_z }}
 */
function init3DThermalState(params) {
  // [Fix-Th1] n_cells → n_cells_bms: BMS 셀 수와 3D 격자 크기를 명확히 구분
  const n_cells_bms = (params.bms_cell_count_s || 96) * (params.bms_cell_count_p || 4);
  const n_x = params.thermal_3d_nx || 8;  // 가로
  const n_y = params.thermal_3d_ny || 6;  // 세로
  const n_z = params.thermal_3d_nz || 2;  // 높이

  // [Fix-3D-Assert] n_cells_bms와 3D 격자 크기 불일치 검출
  // 두 값이 다르면 T_cells 배열이 격자 인덱스와 맞지 않아 셀 단위 핫스팟 추적이 완전히
  // 잘못된 결과를 반환한다. 조용히 넘어가는 console.warn 대신 즉시 throw하여
  // 파라미터 구성 오류를 초기화 시점에 명시적으로 노출한다.
  const n_cells_3d = n_x * n_y * n_z;
  if (n_cells_3d !== n_cells_bms) {
    throw new Error(
      `[init3DThermalState] 파라미터 불일치: ` +
      `BMS 셀 수(bms_cell_count_s × bms_cell_count_p = ${n_cells_bms})가 ` +
      `3D 격자 크기(thermal_3d_nx × thermal_3d_ny × thermal_3d_nz = ${n_cells_3d})와 다릅니다. ` +
      `두 값을 일치시키거나 파라미터를 수정하세요.`
    );
  }
  // 격자 인덱스 기준으로 배열 크기를 통일 (실제 루프가 n_x*n_y*n_z 로 돌기 때문)
  const n_alloc = n_cells_3d;

  const T_amb = params.T_amb || 25;

  return {
    T_cells: new Array(n_alloc).fill(T_amb),
    T_max: T_amb,
    T_min: T_amb,
    T_avg: T_amb,
    T_delta: 0,
    hotspot_index: 0,
    n_x, n_y, n_z
  };
}

// ── getNeighbors3D ──
/**
 * 3D 격자에서 셀 (ix,iy,iz)의 유효한 이웃 셀 선형 인덱스 목록을 반환한다.
 * 격자 경계를 넘는 이웃은 제외(경계 조건: 단열벽).
 *
 * @param {number} ix  x 방향 인덱스
 * @param {number} iy  y 방향 인덱스
 * @param {number} iz  z 방향 인덱스
 * @param {number} nx  x 크기
 * @param {number} ny  y 크기
 * @param {number} nz  z 크기
 * @returns {number[]}  이웃 셀 선형 인덱스 배열 (최대 6개)
 */
function getNeighbors3D(ix, iy, iz, nx, ny, nz) {
  const neighbors = [];
  if (ix > 0) neighbors.push((ix-1) + iy*nx + iz*nx*ny);
  if (ix < nx-1) neighbors.push((ix+1) + iy*nx + iz*nx*ny);
  if (iy > 0) neighbors.push(ix + (iy-1)*nx + iz*nx*ny);
  if (iy < ny-1) neighbors.push(ix + (iy+1)*nx + iz*nx*ny);
  if (iz > 0) neighbors.push(ix + iy*nx + (iz-1)*nx*ny);
  if (iz < nz-1) neighbors.push(ix + iy*nx + (iz+1)*nx*ny);
  return neighbors;
}

// ── update3DThermalStep ──
/**
 * 3D 배터리 열격자를 1스텝 진행한다.
 * 각 셀에 가우시안 분포 열원을 적용하고, 이웃 셀 간 전도와 냉각수 대류를 계산한다.
 * CFL 안정 조건으로 최대 20회 적응형 서브스텝을 적용.
 * [Fix-3D-Norm] 열원 가중치 합산 정규화로 총 투입열이 Q_heat_W와 동일하게 보존.
 *
 * @param {object} thermal_3d   init3DThermalState() 반환 상태
 * @param {number} Q_heat_W     팩 전체 발열량 [W]
 * @param {number} phi_flow     냉각수 유량 방열 계수 (≥0)
 * @param {number} T_coolant    냉각수 온도 [°C]
 * @param {number} dt           타임스텝 [s]
 * @param {object} params       thermal_k_cell, thermal_cell_dx, thermal_C_cell 등
 * @returns {object|null}       갱신된 3D 열 상태 (T_cells, T_max, T_min, T_avg, T_delta, hotspot_index)
 */
function update3DThermalStep(thermal_3d, Q_heat_W, phi_flow, T_coolant, dt, params) {
  if (!thermal_3d) return null;
  const { n_x, n_y, n_z, T_cells } = thermal_3d;
  const N = T_cells.length;
  const k_cell   = params.thermal_k_cell     || 1.8;   // 셀 간 열전도 [W/(m·K)]
  const dx       = params.thermal_cell_dx    || 0.025; // 셀 간 거리 [m]
  const C_cell   = params.thermal_C_cell     || 1200;  // 셀 열용량 [J/K]
  const h_conv   = (params.thermal_h_conv    || 40) * Math.min(3.0, 1.0 + phi_flow * 0.05);
  const A_conv   = params.thermal_A_conv     || 0.002; // 셀 냉각면적 [m²]
  const UA_cell  = h_conv * A_conv;                    // [W/K]
  // [Fix-UA_cond] k_cell/dx는 [W/(m²·K)] — 면접촉 면적 A_cond를 곱해야 [W/K] 차원 성립
  // A_cond 기본값 = dx² (정사각 단면, 0.025m²→6.25e-4 m²)
  const A_cond   = params.thermal_A_cond    || (dx * dx); // 셀 면접촉 면적 [m²]
  const UA_cond  = (k_cell / dx) * A_cond;               // [W/K] — 단위 수정 완료

  // [H4] 3D 노드 루프에 서브스텝 적용 — thermalStep의 동일 패턴
  // τ_cond = C_cell / (UA_cond × 최대_이웃수)   (6면체 최대 이웃 = 6)
  // n_sub  = ceil(dt / (0.5 · τ_cond))  — Euler 안정 조건 (CFL 0.5)
  // 상한 20으로 클램프 (성능 보호; 극단적 dt에서도 과도한 루프 방지)
  const tau_cond_3d = C_cell / (UA_cond * 6 + UA_cell + 1e-9);
  const n_sub_3d    = Math.min(20, Math.max(1, Math.ceil(dt / (0.5 * tau_cond_3d))));
  const dt_sub_3d   = dt / n_sub_3d;

  // 열원 분포 — 중앙 핫존 패턴 (실제 배터리 팩과 유사한 Gaussian)
  const cx = Math.floor(n_x / 2), cy = Math.floor(n_y / 2), cz = Math.floor(n_z / 2);
  const Q_per_cell = Q_heat_W / N;

  // [Fix-3D-Norm] 가우시안 q_w 합산 → 에너지 보존 정규화
  // 정규화 없이는 Σ Q_i = Q_heat_W × (mean q_w) ≠ Q_heat_W → 총 투입열 초과
  // 정규화 인수 q_w_norm: Σ(q_w × q_w_norm) = N → Σ Q_i = Q_per_cell×N = Q_heat_W 보장
  let q_w_sum = 0;
  for (let j = 0; j < N; j++) {
    const jz  = Math.floor(j / (n_x * n_y));
    const jrem = j % (n_x * n_y);
    const jy  = Math.floor(jrem / n_x);
    const jx  = jrem % n_x;
    q_w_sum += Math.exp(-((jx-cx)**2 + (jy-cy)**2 + (jz-cz)**2) / (n_x * 0.5)) * 2.5 + 0.5;
  }
  const q_w_norm = N / q_w_sum; // 정규화 인수 (q_w_sum이 N이면 1.0)

  // [H4] 서브스텝 루프 — 매 sub마다 T_cur로 전도/대류를 재계산하여 Euler 안정성 확보
  let T_cur = T_cells.slice(); // 작업 배열 (원본 불변)
  let T_max = -Infinity, T_min = Infinity, T_sum = 0, hot_idx = 0;

  for (let _sub = 0; _sub < n_sub_3d; _sub++) {
    const T_next = new Array(N);
    for (let i = 0; i < N; i++) {
      const iz = Math.floor(i / (n_x * n_y));
      const rem = i % (n_x * n_y);
      const iy = Math.floor(rem / n_x);
      const ix = rem % n_x;

      // 가우시안 열원 가중치 (핫스팟 강조) — q_w_norm 정규화 적용
      const dist2 = (ix-cx)**2 + (iy-cy)**2 + (iz-cz)**2;
      const q_w   = Math.exp(-dist2 / (n_x * 0.5)) * 2.5 + 0.5;
      const Q_i   = Q_per_cell * q_w * q_w_norm;

      // 이웃 셀 전도 (현재 서브스텝 T_cur 기준)
      const neighbors = getNeighbors3D(ix, iy, iz, n_x, n_y, n_z);
      let dT_cond = 0;
      for (const nIdx of neighbors) {
        dT_cond += UA_cond * (T_cur[nIdx] - T_cur[i]);
      }
      // 냉각제 대류
      const dT_conv = -UA_cell * (T_cur[i] - T_coolant);

      // 서브스텝 오일러 적분 (dt_sub_3d 사용)
      const dT = (Q_i + dT_cond + dT_conv) / C_cell * dt_sub_3d;
      T_next[i] = T_cur[i] + dT;
    }
    T_cur = T_next;
  }

  // 마지막 서브스텝 결과에서 통계 계산
  for (let i = 0; i < N; i++) {
    if (T_cur[i] > T_max) { T_max = T_cur[i]; hot_idx = i; }
    if (T_cur[i] < T_min) T_min = T_cur[i];
    T_sum += T_cur[i];
  }

  return {
    ...thermal_3d,
    T_cells: T_cur,
    T_max:   +T_max.toFixed(2),
    T_min:   +T_min.toFixed(2),
    T_avg:   +(T_sum / N).toFixed(2),
    T_delta: +(T_max - T_min).toFixed(2),
    hotspot_index: hot_idx,
    n_sub_3d,        // [H4] 실제 사용된 서브스텝 수 (진단용)
  };
}

// ── deratingFactor3D ──
/**
 * 3D 열격자 최고 온도(T_max)에 따른 출력 디레이팅 계수를 반환한다.
 * T_hot ≤ T_start → 1.0, T_hot ≥ T_end → d_min, 중간 구간 → 선형 감소.
 *
 * @param {object|null} thermal_3d  update3DThermalStep() 반환 상태 (null 시 1.0 반환)
 * @param {object} params
 *   params.thermal_derate_start [°C] (기본 55)
 *   params.thermal_derate_end   [°C] (기본 75)
 *   params.thermal_derate_min   (기본 0.35)
 * @returns {number}  디레이팅 계수 [d_min, 1.0]
 */
function deratingFactor3D(thermal_3d, params) {
  if (!thermal_3d) return 1.0;
  const T_hot   = thermal_3d.T_max;
  const T_start = params.thermal_derate_start || 55; // °C 시작
  const T_end   = params.thermal_derate_end   || 75; // °C 최소
  const d_min   = params.thermal_derate_min   || 0.35;
  if (T_hot <= T_start) return 1.0;
  if (T_hot >= T_end)   return d_min;
  const ratio = (T_hot - T_start) / (T_end - T_start);
  return 1.0 - ratio * (1.0 - d_min);
}

// ── motorWasteHeatRecovery ──
/**
 * [Th-L2] 모터/인버터 폐열 회수 — 배터리 예열 및 캐빈 HVAC 보조
 *
 * EV 구동계에서 발생하는 모터·인버터 손실열은 냉각수를 통해 외기로 방출되는데,
 * 극저온 주행 시 이 폐열을 (1) 배터리 예열, (2) 히트펌프 열원 보조에 활용하면
 * 히트펌프 전력 소비를 줄이고 배터리 R_int를 낮출 수 있음.
 *
 * 열원 가용량:
 *   Q_motor_waste = P_motor_loss  (모터 동손+철손, 이미 발열로 전환된 열)
 *   Q_inv_waste   = P_inv_loss    (인버터 전도+스위칭 손실)
 *   Q_recoverable = (Q_motor_waste + Q_inv_waste) × η_recovery
 *     η_recovery: 냉각수→히트펌프 열원 전달 효율 (기본 0.70)
 *
 * 배분 우선순위:
 *   1. 배터리 예열: T_batt < T_batt_preheat_target 시, Q_batt_heat 할당
 *   2. 히트펌프 열원 보조: 나머지를 HVAC 측 증발기 열원으로 (COP 개선 효과)
 *
 * @param {number} P_motor_loss  - 모터 총 손실 [W]
 * @param {number} P_inv_loss    - 인버터 총 손실 [W]
 * @param {number} T_batt        - 현재 배터리 팩 온도 [°C]
 * @param {number} T_amb         - 외기 온도 [°C]
 * @param {object} params
 *   params.waste_heat_recovery_enable  : 활성화 (기본 false — opt-in)
 *   params.waste_heat_eta_recovery     : 폐열 전달 효율 (기본 0.70)
 *   params.T_batt_preheat_target       : 배터리 목표 예열 온도 [°C] (기본 15)
 *   params.waste_heat_batt_fraction    : 배터리 예열 우선 배분 비율 (기본 0.40)
 *
 * @returns {{
 *   Q_recoverable, Q_batt_heat, Q_hvac_assist,
 *   COP_boost_delta, active
 * }}
 */
function motorWasteHeatRecovery(P_motor_loss, P_inv_loss, T_batt, T_amb, params, dt) {
  if (!params.waste_heat_recovery_enable || T_amb > 10) {
    return { Q_recoverable: 0, Q_batt_heat: 0, Q_hvac_assist: 0, COP_boost_delta: 0, active: false };
  }
  const dt_eff = (dt != null && dt > 0) ? dt : 1.0; // [Fix-dt] dt 미전달 시 1s 폴백
  const eta   = params.waste_heat_eta_recovery  || 0.70;
  const T_tgt = params.T_batt_preheat_target    || 15;
  const f_bat = params.waste_heat_batt_fraction || 0.40;

  const Q_total       = (P_motor_loss + P_inv_loss) * eta;
  const Q_recoverable = Math.max(0, Q_total);

  const T_deficit = Math.max(0, T_tgt - T_batt);
  // [Fix-dt] 배터리 예열 수요 계산에 실제 dt 반영 (기존 dt=1s 고정 → 가변 dt)
  const Q_batt_heat = T_deficit > 0
    ? Math.min(Q_recoverable * f_bat, (params.C_th_batt || 50000) * T_deficit * dt_eff / 3600)
    : 0;

  const Q_hvac_assist = Math.max(0, Q_recoverable - Q_batt_heat);

  // [Fix-COP-Carnot] Carnot 기반 COP_boost_delta
  //   원리: 폐열 Q_hvac_assist가 증발기에 추가 입열되면 동일 Q_cond를 위한 압축기 일 감소.
  //   히트펌프 에너지 균형: Q_cond = W_comp + Q_evap
  //   폐열 보조 후: W_comp_new = W_comp − Q_hvac_assist
  //   → COP_new = Q_cond / W_comp_new, ΔCOP = COP_new − COP_base
  //   1차 근사: ΔCOP ≈ Q_hvac_assist × (COP_carnot − 1) / P_hvac_nom
  //   (기존 1/P_hvac_nom 비율 대비 COP_carnot 배율만큼 더 정확)
  const T_cond_K   = ((params.T_cabin_ref || 20) + 273.15);          // 응축 온도 [K]
  const T_evap_K   = Math.max(200, (T_amb + 273.15) - 5);            // 증발 온도: 외기 −5 K
  const COP_carnot = T_cond_K / Math.max(1, T_cond_K - T_evap_K);   // 카르노 COP (이상)
  const P_hvac_nom = Math.max(100, params.hvac_max_power || 4500);
  const COP_boost_delta = Q_hvac_assist > 0
    ? +(Q_hvac_assist * (COP_carnot - 1) / P_hvac_nom).toFixed(3)
    : 0;

  return {
    Q_recoverable: +Q_recoverable.toFixed(1),
    Q_batt_heat:   +Q_batt_heat.toFixed(1),
    Q_hvac_assist: +Q_hvac_assist.toFixed(1),
    COP_boost_delta,
    T_deficit:     +T_deficit.toFixed(1),
    active:        Q_recoverable > 0,
  };
}

// ── initCabinThermalState ──
// [Fix-①] dynamics.js 에서 이동 — 실내 열모델은 thermal.js 소관
/**
 * 실내(캐빈) 열 모델 초기 상태를 생성한다.
 * @param {object} params         T_amb [°C]
 * @param {number} [T_cabin_init] 초기 실내 온도 [°C] (미지정 시 T_amb 사용)
 * @returns {{ T_cabin, T_evap, q_hvac_accum, hvac_on }}
 */
function initCabinThermalState(params, T_cabin_init) {
  return {
    T_cabin:      T_cabin_init || params.T_amb || 25,  // 실내 온도 (°C)
    T_evap:       params.T_amb || 25,                   // 증발기 온도 (°C) (에어컨)
    q_hvac_accum: 0,                                    // HVAC 누적 에너지 [J]
    hvac_on:      false,
  };
}

// ── cabinThermalModel ──
// [Fix-①] dynamics.js 에서 이동 — cabin 열역학은 thermal.js 소관
// heatPumpCOP는 동일 파일 내 정의되어 있으므로 import 불필요
/**
 * 실내(캐빈) 온도를 1스텝 진행한다.
 * 열 균형: Q_solar (태양 복사) + Q_ext_transfer (외기 전달) − Q_hvac (냉난방)
 * HVAC: 실내 온도 오차에 비례한 수요 비율로 히트펌프 COP 기반 전력 계산.
 *
 * @param {{ T_cabin, q_hvac_accum, hvac_on }} cabinState  현재 실내 열 상태
 * @param {{ temp_ext, is_sunny, solar_irr_Wm2, v_kmh }} env  환경 정보
 * @param {{ mode: string, target_temp: number }} hvacSetting  HVAC 설정
 * @param {object} params  window_area, cabin_surface, U_insulation, C_cabin 등
 * @param {number} dt      타임스텝 [s]
 * @returns {{ T_cabin, q_hvac_pwr_kW, Q_solar, Q_ext_transfer, Q_hvac, state }}
 */
function cabinThermalModel(cabinState, env, hvacSetting, params, dt) {
  const T_cabin   = cabinState.T_cabin;
  const T_ext     = env.temp_ext  !== undefined ? env.temp_ext : (params.T_amb || 25);
  const is_sunny  = env.is_sunny  !== undefined ? env.is_sunny : false;
  const solar_irr = env.solar_irr_Wm2 || 800;            // 맑은날 태양 복사 강도 [W/m²]
  const v_kmh     = env.v_kmh || 0;

  // ── 파라미터 기본값 ──
  const window_area      = params.window_area     || 2.5;   // 창문 면적 [m²]
  const cabin_surface    = params.cabin_surface   || 8.0;   // 차체 표면적 [m²]
  const U_insulation     = params.U_insulation    || 2.5;   // 단열 계수 [W/(m²·K)]
  const U_window         = params.U_window        || 5.5;   // 창문 열관류율 [W/(m²·K)]
  const solar_gain_coeff = params.solar_gain_coeff || 0.6;  // SHGC (태양 열취득 계수)
  const C_cabin          = params.cabin_heat_cap  || 30000; // 실내 열용량 [J/K]
  const HVAC_max_kW      = params.hvac_max_kW     || 5.0;   // HVAC 최대 출력 [kW]

  // ── 1. 태양 복사열 취득 ──
  const Q_solar = is_sunny
    ? solar_irr * window_area * solar_gain_coeff  // [W]
    : 0;

  // ── 2. 외기-실내 열전달 (차체 + 창문) ──
  //   속도에 따라 환기 손실 추가
  const U_eff   = (U_insulation * (cabin_surface - window_area) + U_window * window_area)
                  / cabin_surface;
  const v_leakage = 0.001 * v_kmh;  // 주행속도에 비례한 실내외 공기 교환 [m³/s 근사]
  const C_air  = 1200;              // 공기 열용량 [J/(m³·K)]
  const Q_leakage = v_leakage * C_air * (T_ext - T_cabin);
  const Q_ext_transfer = U_eff * cabin_surface * (T_ext - T_cabin) + Q_leakage;

  // ── 3. HVAC 제어 ──
  let Q_hvac = 0;
  let q_hvac_pwr = 0;
  const mode = hvacSetting?.mode || (T_cabin > (hvacSetting?.target_temp || 22) + 1 ? 'cool' : 'heat');
  const T_target = hvacSetting?.target_temp || 22;
  const err = T_cabin - T_target;

  if (mode === 'cool' && T_cabin > T_target) {
    const demand_ratio = Math.min(1.0, Math.abs(err) / 5.0);
    const copResultCool = heatPumpCOP(T_ext, 'cool', params);
    Q_hvac = demand_ratio * HVAC_max_kW * 1000;  // [W] 냉각 열 제거
    q_hvac_pwr = Q_hvac / Math.max(1, copResultCool.COP);
  } else if (mode === 'heat' && T_cabin < T_target) {
    const demand_ratio = Math.min(1.0, Math.abs(err) / 5.0);
    const copResultHeat = heatPumpCOP(T_ext, 'heat', params);
    Q_hvac = -(demand_ratio * HVAC_max_kW * 1000);  // 음수 = 가열
    q_hvac_pwr = Math.abs(Q_hvac) / Math.max(1, copResultHeat.COP);
  } else {
    q_hvac_pwr = 0;
  }

  // ── 4. 실내 온도 적분 ──
  const dT_cabin = (Q_solar + Q_ext_transfer - Q_hvac) * dt / C_cabin;
  const T_cabin_new = T_cabin + dT_cabin;

  return {
    T_cabin:      T_cabin_new,
    q_hvac_pwr_kW: q_hvac_pwr / 1000,  // 전력 [kW]
    Q_solar,
    Q_ext_transfer,
    Q_hvac,
    state: {
      ...cabinState,
      T_cabin: T_cabin_new,
      q_hvac_accum: cabinState.q_hvac_accum + q_hvac_pwr * dt,
      hvac_on: q_hvac_pwr > 0,
    },
  };
}

export {
  thermalStep,
  thermalDeratingFactor,
  hvacDetailedPower,
  heatPumpCOP,
  motorWasteHeatRecovery,
  gearThermalStep,
  initCoolantFlowState,
  coolantFlowControl,
  init3DThermalState,
  getNeighbors3D,
  update3DThermalStep,
  deratingFactor3D,
  // [Fix-①] dynamics.js 에서 이전
  initCabinThermalState,
  cabinThermalModel,
  // ✅ RevB: PCM 위상변화 냉각
  initPCMState,
  pcmCoolingStep,
  // ✅ RevB: 냉매 회로 열역학 정밀 모델
  initRefrigerantState,
  refrigerantCycleStep,
  // ✅ RevB: 3D 격자 ↔ 메인루프 통합 래퍼
  thermalStep3DIntegrated,
  // ✅ RevC
  initCellInternalThermalState,   // 셀 수준 내부 열전도 초기화
  cellInternalThermalStep,        // through-plane/in-plane 이방성 2-노드 FD 스텝
  // ✅ RevD — 열관리 도메인 완성도 향상
  thermalResistanceOptimizer,     // 열저항 파라미터 최적화 (R_th_bc/mc/ic 75점 파레토)
  coolingCapacityOptimizer,       // 냉각 용량 최적화 (UA_rad 20점, WLTP 사이클 시뮬)
  derateThresholdSensitivity,     // 디레이팅 임계값 민감도 (T_derate vs 출력손실 vs SOH)
};

// ── initPCMState ── & ── pcmCoolingStep ── (위상변화 냉각 모델)
/**
 * PCM (Phase-Change Material) 냉각 모델 — 잠열 흡수 기반 열완충
 *
 * 용융 분율 f ∈ [0, 1]:
 *   Q_pcm = k_eff × A / d × (T_source − T_melt)  [W]
 *   df/dt = Q_pcm / (m_pcm × L)
 * f < 1:  T_pcm ≈ T_melt (잠열 구간)
 * f = 1:  dT_pcm/dt = Q_pcm / (m_pcm × c_l) (현열 구간)
 *
 * 참고: Zalba et al. (2003), Rao & Wang (2011)
 */
function initPCMState(params) {
  return {
    T_pcm:         params.pcm_T_init ?? (params.pcm_T_melt ?? 40),
    melt_fraction: 0,
  };
}

/**
 * PCM 냉각 1스텝: 잠열 구간(melt_fraction<1, T≥T_melt) → melt_fraction 증가 + T 고정,
 * 응고 구간(melt_fraction>0, T<T_melt) → melt_fraction 감소, 현열 구간 → dT/dt = Q/mc_l.
 *
 * @param {{ T_pcm: number, melt_fraction: number }} pcmState  현재 PCM 상태
 * @param {number} T_source  열원 온도 [°C] (배터리 팩 온도)
 * @param {object} params    pcm_mass, pcm_latent_heat, pcm_T_melt, pcm_k_eff 등
 * @param {number} dt        타임스텝 [s]
 * @returns {{ pcmState, Q_absorbed, T_pcm, melt_fraction }}
 */
function pcmCoolingStep(pcmState, T_source, params, dt) {
  const m_pcm  = params.pcm_mass        ?? 2.0;
  const L_heat = params.pcm_latent_heat ?? 200000;
  const T_melt = params.pcm_T_melt      ?? 42;
  const k_eff  = params.pcm_k_eff       ?? 0.5;
  const A_pcm  = params.pcm_area        ?? 0.04;
  const d_pcm  = params.pcm_thickness   ?? 0.01;
  const c_liq  = params.pcm_c_liquid    ?? 2200;

  let { T_pcm, melt_fraction } = pcmState;
  const Q_cond = k_eff * A_pcm / d_pcm * (T_source - T_pcm);

  let Q_absorbed = 0;
  if (melt_fraction < 1.0 && T_pcm >= T_melt - 0.5) {
    const Q_latent_max = m_pcm * L_heat * (1 - melt_fraction);
    const Q_in   = Math.max(0, Q_cond) * dt;
    const Q_used = Math.min(Q_in, Q_latent_max);
    melt_fraction = Math.min(1.0, melt_fraction + Q_used / (m_pcm * L_heat));
    T_pcm = T_melt;
    Q_absorbed = Q_used / dt;
  } else if (melt_fraction > 0.0 && T_pcm < T_melt - 0.5) {
    const Q_solidify = Math.min(0, Q_cond) * dt;
    melt_fraction = Math.max(0, melt_fraction - Math.abs(Q_solidify) / (m_pcm * L_heat));
    Q_absorbed = Q_solidify / dt;
    T_pcm += Q_cond / (m_pcm * c_liq) * dt;
  } else {
    T_pcm += Q_cond / (m_pcm * c_liq) * dt;
    Q_absorbed = Q_cond;
  }

  return { pcmState: { T_pcm, melt_fraction }, Q_absorbed, T_pcm, melt_fraction };
}

// ── initRefrigerantState ── & ── refrigerantCycleStep ── (증기 압축 냉동 사이클)
/**
 * 증기 압축 냉동 사이클 정밀 COP 모델 (R1234yf 계열 엔탈피 근사)
 *
 * Q_evap = m_dot × (h_g(T_evap) − h_f(T_cond))
 * W_comp = m_dot × (h_g(T_cond) − h_g(T_evap)) / eta_comp
 * COP_cool = Q_evap / W_comp,  COP_heat = (Q_evap + W_comp) / W_comp
 *
 * 참고: ASHRAE Fundamentals 2021 Ch.2; Qi et al. (2020)
 */
function initRefrigerantState(params) {
  return {
    T_evap_cur: params.T_amb ?? 25,
    T_cond_cur: params.T_cabin_ref ?? 20,
    m_dot:      params.refrigerant_m_dot ?? 0.02,
    mode:       'off',
  };
}

/**
 * 증기 압축 사이클 1스텝: 냉매 엔탈피로 Q_evap, W_comp, COP를 계산한다.
 * P_comp_max로 압축기 출력을 제한하고 실제 질량 유량(m_dot_eff)을 조절(스로틀링).
 *
 * @param {object} rfState     initRefrigerantState() 상태
 * @param {number} T_evap      증발기 온도 [°C]
 * @param {number} T_cond      응축기 온도 [°C]
 * @param {number} P_comp_max  압축기 최대 전력 [W]
 * @param {object} params      ref_h_g0, ref_h_f0, ref_cp_vap, ref_eta_comp 등
 * @param {number} dt          타임스텝 [s]
 * @returns {{ rfState, COP, COP_cool, COP_heat, Q_evap, Q_cond, W_comp, m_dot_eff }}
 */
function refrigerantCycleStep(rfState, T_evap, T_cond, P_comp_max, params, dt) {
  const h_g0     = params.ref_h_g0     ?? 398000;
  const h_f0     = params.ref_h_f0     ?? 196000;
  const cp_vap   = params.ref_cp_vap   ?? 1100;
  const cp_liq   = params.ref_cp_liq   ?? 1460;
  const eta_comp = params.ref_eta_comp ?? 0.75;
  const m_dot    = rfState.m_dot;

  const h_g_evap = h_g0 + cp_vap * T_evap;
  const h_f_cond = h_f0 + cp_liq * T_cond;
  const h_g_cond = h_g0 + cp_vap * T_cond;

  const dh_isentropic = Math.max(0, h_g_cond - h_g_evap);
  const dh_actual     = dh_isentropic / eta_comp;
  const Q_evap_spec   = Math.max(0, h_g_evap - h_f_cond);
  const W_comp_spec   = Math.max(1,  dh_actual);

  const W_comp_unlimited = m_dot * W_comp_spec;
  const throttle = P_comp_max > 0 ? Math.min(1.0, P_comp_max / (W_comp_unlimited + 1)) : 1.0;
  const m_dot_eff = m_dot * throttle;

  const Q_evap  = m_dot_eff * Q_evap_spec;
  const W_comp  = m_dot_eff * W_comp_spec;
  const Q_cond_out = Q_evap + W_comp;
  const COP_cool = W_comp > 1 ? Q_evap / W_comp : 0;
  const COP_heat = W_comp > 1 ? Q_cond_out / W_comp : 0;
  const mode = params.refrigerant_mode ?? 'cooling';

  return {
    rfState: { ...rfState, T_evap_cur: T_evap, T_cond_cur: T_cond, mode },
    COP:  mode === 'heating' ? COP_heat : COP_cool,
    COP_cool, COP_heat, Q_evap, Q_cond: Q_cond_out, W_comp, m_dot_eff,
  };
}

// ── thermalStep3DIntegrated ── (3D 격자 ↔ 메인 루프 완전 통합)
/**
 * 3D 열격자를 메인 시뮬레이션 루프에 연동하는 래퍼
 *
 * 실행 순서:
 *   1. thermalStep()       — 집중 4노드 갱신
 *   2. pcmCoolingStep()    — PCM 잠열 흡수 (활성 시)
 *   3. refrigerantCycleStep() — 냉매 사이클 (활성 시)
 *   4. update3DThermalStep() — 3D 격자 갱신 + 평균→T_batt 피드백
 */
function thermalStep3DIntegrated(
  thState, thermal_3d, pcmState, rfState,
  Q_batt, Q_inv, Q_motor, v, params, dt, phi_flow
) {
  const newTh = thermalStep(thState, Q_batt, Q_inv, Q_motor, v, params, dt, phi_flow);

  let Q_pcm = 0, newPcm = pcmState;
  if (pcmState && params.pcm_enable) {
    const r = pcmCoolingStep(pcmState, newTh.T_batt, params, dt);
    newPcm = r.pcmState; Q_pcm = r.Q_absorbed;
    const C_batt = params.C_th_batt || 50000;
    newTh.T_batt = Math.max(params.T_amb ?? 20, newTh.T_batt - Q_pcm / C_batt * dt);
  }

  let COP_rf = 0, newRf = rfState;
  if (rfState && params.refrigerant_enable) {
    const P_comp = params.hvac_compressor_max_W ?? 3000;
    const r = refrigerantCycleStep(rfState, newTh.T_batt, params.T_amb ?? 25, P_comp, params, dt);
    newRf = r.rfState; COP_rf = r.COP;
    const C_cool = params.C_th_cool || 8000;
    newTh.T_cool = Math.max((params.T_amb ?? 20) - 5, newTh.T_cool - r.Q_evap / C_cool * dt);
  }

  let T_batt_3d_mean = newTh.T_batt, new3D = thermal_3d;
  if (thermal_3d && params.thermal_3d_enable !== false) {
    new3D = update3DThermalStep(thermal_3d, Q_batt, phi_flow ?? 1.0, newTh.T_cool, dt, params);
    if (new3D && new3D.T && new3D.T.length > 0) {
      T_batt_3d_mean = new3D.T.reduce((s, t) => s + t, 0) / new3D.T.length;
      newTh.T_batt = T_batt_3d_mean;
    }
  }

  return { thState: newTh, thermal_3d: new3D, pcmState: newPcm, rfState: newRf,
           T_batt_3d_mean, Q_pcm, COP_rf };
}
// ══════════════════════════════════════════════════════════════════════
// ✅ RevC 미구현→구현 ⑥ — 배터리 셀 내부 열전도 모델 (셀 수준)
// ══════════════════════════════════════════════════════════════════════
//
// 기존 한계: 배터리 팩 전체를 집중 열용량 1개로 처리
//   → 셀 내부 온도 구배 없음, in-plane / through-plane 이방성 무시
//
// 개선: 원통형/파우치 셀의 2층 열전도 모델
//   - r 방향 (through-plane): k_r ≈ 0.4–1.5 W/(m·K)  (젤리롤 적층 방향)
//   - z 방향 (in-plane):      k_z ≈ 8–30  W/(m·K)     (알루미늄 탭 방향)
//
// 이산화 (2-노드 단순 모델, 셀 중심 ↔ 표면):
//   T_core: 셀 코어 온도
//   T_surf: 셀 표면 온도 (냉각매체와 직접 접촉)
//
//   C_core · dT_core/dt = Q_gen − (T_core − T_surf) / R_cell
//   C_surf · dT_surf/dt = (T_core − T_surf) / R_cell − (T_surf − T_cool) / R_contact
//
//   R_cell = L_r / (k_r · A_cell)        [K/W] — through-plane 열저항
//   R_contact = R_contact_base / n_cells  [K/W] — 팩-냉각판 접촉 열저항
//
// 파라미터(params):
//   cell_k_r [W/(m·K)]      — through-plane 열전도율 (기본 1.0)
//   cell_k_z [W/(m·K)]      — in-plane 열전도율 (기본 20.0)
//   cell_r_outer [m]        — 셀 외경/2 (원통형) 또는 두께/2 (파우치) (기본 9e-3)
//   cell_L_r [m]            — 열전도 경로 길이 (기본 = cell_r_outer)
//   cell_A_contact [m²]     — 셀-냉각판 접촉 면적 (기본 30e-4)
//   cell_h_contact [W/(m²·K)] — 셀-냉각판 접촉 열전달 계수 (기본 800)
//   cell_mass [kg]          — 단일 셀 질량 (기본 0.065, 21700형 기준)
//   cell_cp [J/(kg·K)]      — 셀 비열 (기본 1020)
//   bms_cell_count_s/p      — 직/병렬 셀 수
// ══════════════════════════════════════════════════════════════════════

/** 셀 내부 열전도 상태 초기화 */
function initCellInternalThermalState(params) {
  const T_amb   = params.T_amb || 25;
  const n_s     = params.bms_cell_count_s || 96;
  const n_p     = params.bms_cell_count_p || 4;
  const n_cells = n_s * n_p;
  return {
    T_core: new Array(n_cells).fill(T_amb),  // 셀 코어 온도 [°C]
    T_surf: new Array(n_cells).fill(T_amb),  // 셀 표면 온도 [°C]
    T_core_max: T_amb,
    T_surf_max: T_amb,
    delta_T_max: 0,      // 최대 코어-표면 온도 차 [K]
    n_cells,
  };
}

/** 셀 내부 열전도 스텝 (explicit FD, 2-노드)
 * @param {object} cell_th_state   — initCellInternalThermalState() 상태
 * @param {number} Q_batt_total    — 팩 전체 발열량 [W]
 * @param {number} T_cool          — 냉각수 온도 [°C]
 * @param {number[]} Q_cell_dist   — 셀별 발열 분포 [W] (없으면 균일 가정)
 * @param {number} dt              — 시간 스텝 [s]
 * @param {object} params
 */
function cellInternalThermalStep(cell_th_state, Q_batt_total, T_cool, Q_cell_dist, dt, params) {
  const n_cells = cell_th_state.n_cells;
  const n_s     = params.bms_cell_count_s || 96;
  const n_p     = params.bms_cell_count_p || 4;

  // ── 셀 물성 ─────────────────────────────────────────────────
  const k_r         = params.cell_k_r         || 1.0;    // through-plane [W/(m·K)]
  const cell_r      = params.cell_r_outer      || 9e-3;   // 반경 [m]
  const L_r         = params.cell_L_r          || cell_r; // 열전도 거리 [m]
  const A_cell_lat  = Math.PI * (cell_r * 2) * (params.cell_height || 0.070); // 측면 면적 [m²]
  const A_contact   = params.cell_A_contact    || 30e-4;  // [m²]
  const h_contact   = params.cell_h_contact    || 800;    // [W/(m²·K)]

  const m_cell      = params.cell_mass         || 0.065;  // [kg]
  const cp_cell     = params.cell_cp           || 1020;   // [J/(kg·K)]

  // 열저항 [K/W]
  const R_cell_tp  = L_r / Math.max(1e-9, k_r * A_cell_lat);   // through-plane 저항
  const R_contact  = 1.0 / Math.max(1e-9, h_contact * A_contact); // 접촉 저항

  // 열용량 [J/K]
  const C_core = m_cell * cp_cell * 0.75;  // 코어: 전체 열용량의 75%
  const C_surf = m_cell * cp_cell * 0.25;  // 표면: 나머지 25%

  // 셀별 발열 [W]
  const Q_per_cell = Q_batt_total / Math.max(1, n_cells);

  const T_core_new = new Array(n_cells);
  const T_surf_new = new Array(n_cells);

  let T_core_max = -Infinity, T_surf_max = -Infinity, delta_T_max = 0;

  for (let i = 0; i < n_cells; i++) {
    const Q_i = Q_cell_dist ? (Q_cell_dist[i] || Q_per_cell) : Q_per_cell;
    const Tc  = cell_th_state.T_core[i];
    const Ts  = cell_th_state.T_surf[i];

    // 코어→표면 열 흐름
    const Q_cs = (Tc - Ts) / R_cell_tp;
    // 표면→냉각수 열 흐름
    const Q_sc = (Ts - T_cool) / R_contact;

    // 오일러 적분
    const dTc = (Q_i - Q_cs) / C_core * dt;
    const dTs = (Q_cs - Q_sc) / C_surf * dt;

    T_core_new[i] = Tc + dTc;
    T_surf_new[i] = Ts + dTs;

    if (T_core_new[i] > T_core_max) T_core_max = T_core_new[i];
    if (T_surf_new[i] > T_surf_max) T_surf_max = T_surf_new[i];
    const dT_i = T_core_new[i] - T_surf_new[i];
    if (dT_i > delta_T_max) delta_T_max = dT_i;
  }

  cell_th_state.T_core = T_core_new;
  cell_th_state.T_surf = T_surf_new;
  cell_th_state.T_core_max = +T_core_max.toFixed(3);
  cell_th_state.T_surf_max = +T_surf_max.toFixed(3);
  cell_th_state.delta_T_max = +delta_T_max.toFixed(3);

  // in-plane 열 흐름 정보 (정보용, 별도 이산화 없이 추정)
  const k_z       = params.cell_k_z || 20.0;   // in-plane [W/(m·K)]
  const A_z       = Math.PI * cell_r * cell_r;  // 탭 단면 [m²]
  const L_z       = params.cell_height || 0.070;
  const R_cell_ip = L_z / Math.max(1e-9, k_z * A_z);  // in-plane 저항
  // 대표 in-plane 온도 구배 (탭→중심 방향)
  const Q_rep  = Q_per_cell;
  const dT_inplane = Q_rep * R_cell_ip;        // 탭-중심 온도 차 추정 [K]

  return {
    T_core_max:   cell_th_state.T_core_max,
    T_surf_max:   cell_th_state.T_surf_max,
    delta_T_max:  cell_th_state.delta_T_max,
    dT_inplane:   +dT_inplane.toFixed(3),     // in-plane 온도 차 [K]
    R_cell_tp:    +R_cell_tp.toFixed(4),
    R_contact:    +R_contact.toFixed(4),
    R_cell_ip:    +R_cell_ip.toFixed(4),
    k_r, k_z,
  };
}

// ══════════════════════════════════════════════════════════════════════
// ✅ RevD — 열저항 파라미터 최적화 (partial → impl)
/**
 * 열저항 파라미터 파레토 최적화 탐색 (R_th_bc × R_th_mc × R_th_ic = 5×5×3 = 75점).
 * 목표: T_batt_peak / T_motor_peak 최소화 (2목적 파레토 전선).
 * 구동 조건: 정적 최대 부하 × 120 s 시뮬레이션. 펌프 전력 Dittus-Boelter 역산으로 추정.
 *
 * @param {object} baseParams  차량 열 파라미터 (T_amb, C_th_*, UA_rad, phi_* 등)
 * @param {object} [opts]      rbc_range, rmc_range, ric_range, Q_batt/motor/inv, v_ref 등
 * @returns {{ pareto, all, best_batt, best_motor, best_balanced, meta }}
 */
function thermalResistanceOptimizer(baseParams, opts = {}) {
  // ── 탐색 격자 ──────────────────────────────────────────────────────
  const rbc_range = opts.rbc_range || [0.010, 0.018, 0.025, 0.035, 0.050]; // R_th_bc [K/W]
  const rmc_range = opts.rmc_range || [0.015, 0.025, 0.040, 0.060, 0.090]; // R_th_mc [K/W]
  const ric_range = opts.ric_range || [0.020, 0.040, 0.070];               // R_th_ic [K/W]

  // ── 구동 조건 ──────────────────────────────────────────────────────
  const dt     = 1.0;   // 적분 스텝 [s] (열 시정수 >> dt이므로 1 s 충분)
  const T_sim  = 120;   // 시뮬 시간 [s] — 최대 부하 2분 구간
  const Q_batt  = opts.Q_batt  ?? 4500;   // 배터리 발열 [W] (최대 연속 부하)
  const Q_motor = opts.Q_motor ?? 2800;   // 모터 발열 [W]
  const Q_inv   = opts.Q_inv   ?? 800;    // 인버터 발열 [W]
  const v_ref   = opts.v_ref   ?? 100;    // 기준 속도 [km/h] → phi_speed 계산
  const v_ms    = v_ref / 3.6;

  // ── 단순 4-노드 열 시뮬레이션 (thermalStep 핵심 로직 인라인) ──────
  const simThermal = (p) => {
    const phi_speed = 1 + (p.phi_speed || 0.03) * Math.min(v_ms / 30, 2);
    const phi = Math.min(phi_speed, p.phi_max || 4.0);
    let Tb = p.T_amb, Ti = p.T_amb, Tm = p.T_amb, Tc = p.T_amb;
    let Tb_peak = Tb, Tm_peak = Tm, Ti_peak = Ti;

    const UA_rad   = p.UA_rad || 180;
    const C_batt   = p.C_th_batt  || 50000;
    const C_inv    = p.C_th_inv   || 3000;
    const C_motor  = p.C_th_motor || 12000;
    const C_cool   = p.C_th_cool  || 8000;
    const R_th_ba  = p.R_th_ba || 5.0;
    const T_amb    = p.T_amb || 25;

    for (let t = 0; t < T_sim; t += dt) {
      const qbc  = (Tb - Tc) / p.R_th_bc;
      const qba  = (Tb - T_amb) / R_th_ba;
      const qic  = (Ti - Tc) / p.R_th_ic;
      const qmc  = (Tm - Tc) / p.R_th_mc;
      const qrad = UA_rad * phi * (Tc - T_amb);

      Tb += (Q_batt  - qbc - qba) / C_batt  * dt;
      Ti += (Q_inv   - qic)       / C_inv   * dt;
      Tm += (Q_motor - qmc)       / C_motor * dt;
      Tc  = Math.max(T_amb - 5,
            Tc + (qbc + qic + qmc - qrad) / C_cool * dt);

      if (Tb > Tb_peak) Tb_peak = Tb;
      if (Tm > Tm_peak) Tm_peak = Tm;
      if (Ti > Ti_peak) Ti_peak = Ti;
    }
    return { T_batt_peak: +Tb_peak.toFixed(2), T_motor_peak: +Tm_peak.toFixed(2), T_inv_peak: +Ti_peak.toFixed(2) };
  };

  // ── 펌프 전력 추정 (R_th_bc 감소 → 유량 증가 → 펌프 소비 증가) ──
  // P_pump ∝ (R_th_bc_ref / R_th_bc)^(1/0.6) — Dittus-Boelter 역산
  const R_th_bc_ref = 0.025;  // 기준 열저항
  const P_pump_base = opts.P_pump_base ?? 200; // 기준 펌프 전력 [W]
  const calcPump = (rbc) =>
    P_pump_base * Math.pow(R_th_bc_ref / Math.max(rbc, 1e-6), 1 / 0.6);

  // ── 격자 탐색 ─────────────────────────────────────────────────────
  const allPoints = [];
  for (const rbc of rbc_range) {
    for (const rmc of rmc_range) {
      for (const ric of ric_range) {
        const p = { ...baseParams, R_th_bc: rbc, R_th_mc: rmc, R_th_ic: ric };
        const { T_batt_peak, T_motor_peak, T_inv_peak } = simThermal(p);
        const P_pump = +calcPump(rbc).toFixed(1);
        allPoints.push({ R_th_bc: rbc, R_th_mc: rmc, R_th_ic: ric,
                         T_batt_peak, T_motor_peak, T_inv_peak, P_pump });
      }
    }
  }

  // ── 파레토 전선: T_batt_peak ↓ / T_motor_peak ↓ (2목적) ──────────
  const pareto = allPoints.filter(a =>
    !allPoints.some(b =>
      b !== a &&
      b.T_batt_peak  <= a.T_batt_peak  &&
      b.T_motor_peak <= a.T_motor_peak &&
      (b.T_batt_peak < a.T_batt_peak || b.T_motor_peak < a.T_motor_peak)
    )
  ).sort((a, b) => a.T_batt_peak - b.T_batt_peak);

  const best_batt   = allPoints.reduce((b, a) => a.T_batt_peak  < b.T_batt_peak  ? a : b);
  const best_motor  = allPoints.reduce((b, a) => a.T_motor_peak < b.T_motor_peak ? a : b);
  const max_tb = Math.max(...allPoints.map(p => p.T_batt_peak));
  const max_tm = Math.max(...allPoints.map(p => p.T_motor_peak));
  const best_balanced = allPoints.reduce((b, a) => {
    const da = (a.T_batt_peak / max_tb) ** 2 + (a.T_motor_peak / max_tm) ** 2;
    const db = (b.T_batt_peak / max_tb) ** 2 + (b.T_motor_peak / max_tm) ** 2;
    return da < db ? a : b;
  });

  return {
    pareto,
    all: allPoints,
    best_batt,        // 배터리 피크온도 최소화
    best_motor,       // 모터 피크온도 최소화
    best_balanced,    // 정규화 유클리드 균형해
    meta: {
      n_total:   allPoints.length,
      n_pareto:  pareto.length,
      T_sim,
      Q_batt, Q_motor, Q_inv,
      v_ref_kmh: v_ref,
    },
  };
}

/**
 * 냉각 용량 최적화: T_batt_peak ≤ T_peak_limit 조건을 만족하는 최소 UA_rad를 탐색한다.
 * UA_rad 격자(기본 20점)에서 WLTP 축약 사이클(30분) 전체를 시뮬레이션.
 * 제약 충족 결과를 feasible/infeasible로 분리하고 민감도(ΔT/ΔUA)를 분석한다.
 *
 * @param {object} baseParams  차량 열 파라미터
 * @param {object} [opts]      ua_min/max/n_ua, T_limit, Q_profiles, v_ref 등
 * @returns {{ feasible, infeasible, min_UA, best_thermal, sensitivity, meta }}
 */
function coolingCapacityOptimizer(baseParams, opts = {}) {
  // ── UA_rad 탐색 격자 ───────────────────────────────────────────────
  const ua_min   = opts.ua_min   ?? 50;    // [W/K]
  const ua_max   = opts.ua_max   ?? 600;   // [W/K]
  const n_ua     = opts.n_ua     ?? 20;    // 탐색 포인트 수
  const T_limit  = opts.T_limit  ?? 45.0; // T_batt_peak 허용 상한 [°C]

  const ua_range = [];
  for (let i = 0; i < n_ua; i++) {
    ua_range.push(+(ua_min + (ua_max - ua_min) * i / (n_ua - 1)).toFixed(1));
  }

  // ── WLTP급 부하 프로파일 (정현파 근사 — 도심/교외/고속 3구간) ─────
  // Q_batt(t): 도심 저부하 → 교외 중간 → 고속 고부하 → 재생제동
  const dt     = 2.0;      // [s]
  const T_sim  = 1800;     // [s] WLTP 축약 30분
  const N_steps = T_sim / dt;
  const T_amb   = baseParams.T_amb || 25;

  // 구간별 최대 발열량 [W]
  const Q_profiles = opts.Q_profiles || [
    // [t_start, t_end, Q_batt_W, Q_motor_W, Q_inv_W, v_kmh]
    [0,    300,  1200, 800,  250,  30],   // 도심
    [300,  900,  2800, 1800, 500,  80],   // 교외
    [900,  1500, 4500, 2800, 800,  120],  // 고속
    [1500, 1800, 500,  200,  100,  30],   // 감속·재생
  ];

  const getLoad = (t_s) => {
    for (const [t0, t1, qb, qm, qi, v] of Q_profiles) {
      if (t_s >= t0 && t_s < t1) return { Q_batt: qb, Q_motor: qm, Q_inv: qi, v };
    }
    return { Q_batt: 500, Q_motor: 200, Q_inv: 100, v: 30 };
  };

  // ── 단일 UA_rad로 전체 사이클 시뮬레이션 ─────────────────────────
  const simCycle = (UA_rad) => {
    const p = { ...baseParams, UA_rad };
    const phi_speed_fn = (v) => 1 + (p.phi_speed || 0.03) * Math.min(v / 3.6 / 30, 2);
    const R_th_bc = p.R_th_bc || 0.025;
    const R_th_mc = p.R_th_mc || 0.040;
    const R_th_ic = p.R_th_ic || 0.040;
    const R_th_ba = p.R_th_ba || 5.0;
    const C_batt  = p.C_th_batt  || 50000;
    const C_inv   = p.C_th_inv   || 3000;
    const C_motor = p.C_th_motor || 12000;
    const C_cool  = p.C_th_cool  || 8000;

    let Tb = T_amb, Ti = T_amb, Tm = T_amb, Tc = T_amb;
    let Tb_peak = Tb, Tm_peak = Tm;
    let E_cooling = 0;  // 냉각에 쓰인 에너지 [J]

    for (let step = 0; step < N_steps; step++) {
      const t_s = step * dt;
      const { Q_batt, Q_motor, Q_inv, v } = getLoad(t_s);
      const phi = Math.min(phi_speed_fn(v), p.phi_max || 4.0);

      const qbc  = (Tb - Tc) / R_th_bc;
      const qba  = (Tb - T_amb) / R_th_ba;
      const qic  = (Ti - Tc) / R_th_ic;
      const qmc  = (Tm - Tc) / R_th_mc;
      const qrad = UA_rad * phi * (Tc - T_amb);

      Tb += (Q_batt  - qbc - qba) / C_batt  * dt;
      Ti += (Q_inv   - qic)       / C_inv   * dt;
      Tm += (Q_motor - qmc)       / C_motor * dt;
      Tc  = Math.max(T_amb - 5,
            Tc + (qbc + qic + qmc - qrad) / C_cool * dt);

      if (Tb > Tb_peak) Tb_peak = Tb;
      if (Tm > Tm_peak) Tm_peak = Tm;
      if (qrad > 0) E_cooling += qrad * dt;  // 라디에이터 방열 에너지 [J]
    }
    return {
      UA_rad,
      T_batt_peak:  +Tb_peak.toFixed(2),
      T_motor_peak: +Tm_peak.toFixed(2),
      E_cooling_kJ: +(E_cooling / 1000).toFixed(1),
      feasible:     Tb_peak <= T_limit,
    };
  };

  // ── 전체 격자 시뮬레이션 ─────────────────────────────────────────
  const allPoints = ua_range.map(simCycle);
  const feasible   = allPoints.filter(p => p.feasible);
  const infeasible = allPoints.filter(p => !p.feasible);

  // 최소 UA (제약 만족 중 가장 작은 값 → 비용 최소)
  const min_UA = feasible.length > 0
    ? feasible.reduce((b, a) => a.UA_rad < b.UA_rad ? a : b)
    : null;

  // 최저 T_peak (성능 여유 최대)
  const best_thermal = allPoints.reduce((b, a) => a.T_batt_peak < b.T_batt_peak ? a : b);

  // 민감도: dT_batt_peak / dUA_rad [K / (W/K)]
  const sensitivity = allPoints.map((p, i) => {
    if (i === 0) return { UA_rad: p.UA_rad, dT_dUA: null };
    const dUA = allPoints[i].UA_rad - allPoints[i - 1].UA_rad;
    const dT  = allPoints[i].T_batt_peak - allPoints[i - 1].T_batt_peak;
    return { UA_rad: p.UA_rad, dT_dUA: +(dT / dUA).toFixed(4) };
  });

  return {
    feasible,
    infeasible,
    all: allPoints,
    min_UA,           // 비용 최적 (최소 UA_rad로 제약 만족)
    best_thermal,     // 열 최적 (T_peak 최소)
    sensitivity,      // dT/dUA 민감도 프로파일
    meta: {
      n_total:  allPoints.length,
      n_feasible: feasible.length,
      T_limit,
      T_sim,
      ua_min, ua_max,
    },
  };
}

/**
 * 디레이팅 임계값 민감도 분석 (T_derate_batt × T_derate_motor = 6×5 = 30점).
 * 목표: 디레이팅 진입 온도 vs 출력 손실(%) vs 배터리 SOH 수명 트레이드오프 정량화.
 * SOH 손상: k_SOH = A_soh × exp(−Ea/(R×T_K)),  Arrhenius 근사 누적 손상.
 * 출력 손실: thermalDeratingFactor 적분 대비 기준 출력과의 차이.
 *
 * @param {object} baseParams  차량 열 파라미터
 * @param {object} [opts]      Td_batt/motor 탐색 범위, Ea, A_soh, Q_batt/motor/inv, v_ref
 * @returns {{ all, best_power, best_soh, pareto, meta }}
 */
function derateThresholdSensitivity(baseParams, opts = {}) {
  // ── 탐색 격자 ────────────────────────────────────────────────────
  const Td_batt_range  = opts.Td_batt  || [35, 40, 45, 50, 55, 60]; // [°C]
  const Td_motor_range = opts.Td_motor || [110, 120, 130, 140, 150]; // [°C]

  // ── Arrhenius SOH 손상 상수 ───────────────────────────────────────
  // 참고: 리튬이온 용량 감소 Arrhenius — E_a ≈ 30–50 kJ/mol
  const R_gas = 8.314;         // [J/(mol·K)]
  const E_a   = opts.Ea ?? 38000; // [J/mol] — 활성화 에너지
  const A_soh = opts.A_soh ?? 1.0; // 빈도 인자 (정규화)

  const arrheniusDamage = (T_batt_C) => {
    const T_K = T_batt_C + 273.15;
    return A_soh * Math.exp(-E_a / (R_gas * T_K));
  };

  // ── 구동 사이클: 정속 최대 부하 120 s 시뮬레이션 ─────────────────
  const dt     = 1.0;
  const T_sim  = 120;
  const Q_batt_full  = opts.Q_batt  ?? 4500;   // 최대 부하 발열 [W]
  const Q_motor_full = opts.Q_motor ?? 2800;
  const Q_inv_full   = opts.Q_inv   ?? 800;
  const v_ms  = (opts.v_ref ?? 100) / 3.6;

  const simDerate = (Td_batt, Td_motor) => {
    const p = {
      ...baseParams,
      T_batt_derate_start: Td_batt,
      T_batt_cutoff:       Td_batt + 15,
      T_motor_derate_start: Td_motor,
      T_motor_cutoff:       Td_motor + 20,
    };
    const phi_speed = 1 + (p.phi_speed || 0.03) * Math.min(v_ms / 30, 2);
    const phi = Math.min(phi_speed, p.phi_max || 4.0);
    const R_th_bc = p.R_th_bc || 0.025;
    const R_th_mc = p.R_th_mc || 0.040;
    const R_th_ic = p.R_th_ic || 0.040;
    const R_th_ba = p.R_th_ba || 5.0;
    const UA_rad  = p.UA_rad  || 180;
    const C_batt  = p.C_th_batt  || 50000;
    const C_inv   = p.C_th_inv   || 3000;
    const C_motor = p.C_th_motor || 12000;
    const C_cool  = p.C_th_cool  || 8000;
    const T_amb   = p.T_amb || 25;

    let Tb = T_amb, Ti = T_amb, Tm = T_amb, Tc = T_amb;
    let E_delivered = 0;   // 실제 전달 에너지 [J]
    let E_max = 0;         // 디레이팅 없을 시 최대 에너지 [J]
    let damage_acc = 0;    // 누적 Arrhenius 손상

    for (let t = 0; t < T_sim; t += dt) {
      // 디레이팅 인자 계산
      const f_batt = Tb < Td_batt ? 1.0
        : Tb > (Td_batt + 15) ? 0.0
        : 1.0 - (Tb - Td_batt) / 15;
      const f_motor = Tm < Td_motor ? 1.0
        : Tm > (Td_motor + 20) ? 0.0
        : 1.0 - (Tm - Td_motor) / 20;
      const f = Math.max(0, Math.min(f_batt, f_motor));

      // 실제 발열량 (디레이팅 적용)
      const Q_b = Q_batt_full  * f;
      const Q_m = Q_motor_full * f;
      const Q_i = Q_inv_full   * f;

      const qbc  = (Tb - Tc) / R_th_bc;
      const qba  = (Tb - T_amb) / R_th_ba;
      const qic  = (Ti - Tc) / R_th_ic;
      const qmc  = (Tm - Tc) / R_th_mc;
      const qrad = UA_rad * phi * (Tc - T_amb);

      Tb += (Q_b - qbc - qba) / C_batt  * dt;
      Ti += (Q_i - qic)       / C_inv   * dt;
      Tm += (Q_m - qmc)       / C_motor * dt;
      Tc  = Math.max(T_amb - 5,
            Tc + (qbc + qic + qmc - qrad) / C_cool * dt);

      E_delivered += Q_b * dt;
      E_max       += Q_batt_full * dt;
      damage_acc  += arrheniusDamage(Tb) * dt;
    }

    const power_loss_pct = +((1 - E_delivered / Math.max(E_max, 1)) * 100).toFixed(2);
    // SOH 점수: 손상 누적치 역수 정규화 (0=최악, 1=최선)
    // 기준: Td_batt=35 → 가장 빠른 디레이팅 → 손상 최소, 출력 최대 손실
    const soh_index = +(1 / Math.max(damage_acc, 1e-20)).toFixed(6);

    return {
      Td_batt, Td_motor,
      power_loss_pct,
      soh_index,
      damage_acc: +damage_acc.toFixed(6),
    };
  };

  // ── 격자 탐색 ────────────────────────────────────────────────────
  const allPoints = [];
  for (const Td_b of Td_batt_range) {
    for (const Td_m of Td_motor_range) {
      allPoints.push(simDerate(Td_b, Td_m));
    }
  }

  // 파레토: power_loss_pct ↓ / damage_acc ↓ (SOH 손상 최소)
  const pareto = allPoints.filter(a =>
    !allPoints.some(b =>
      b !== a &&
      b.power_loss_pct <= a.power_loss_pct &&
      b.damage_acc     <= a.damage_acc &&
      (b.power_loss_pct < a.power_loss_pct || b.damage_acc < a.damage_acc)
    )
  ).sort((a, b) => a.power_loss_pct - b.power_loss_pct);

  const best_power = allPoints.reduce((b, a) => a.power_loss_pct < b.power_loss_pct ? a : b);
  const best_soh   = allPoints.reduce((b, a) => a.damage_acc    < b.damage_acc    ? a : b);

  return {
    all: allPoints,
    pareto,
    best_power,   // 출력 손실 최소 (디레이팅 임계값 높음 → SOH 손상 ↑)
    best_soh,     // SOH 손상 최소 (디레이팅 임계값 낮음 → 출력 손실 ↑)
    meta: {
      n_total:  allPoints.length,
      n_pareto: pareto.length,
      T_sim,
      Q_batt_full, Q_motor_full,
      Ea_J_per_mol: E_a,
    },
  };
}